import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { Breadcrumb } from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import { dramas, searchDramas, tags } from '@/mocks/dramas';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [draft, setDraft] = useState(keyword);

  useEffect(() => {
    setDraft(keyword);
  }, [keyword]);

  const results = useMemo(() => searchDramas(keyword), [keyword]);
  const suggested = useMemo(() => [...dramas].sort((a, b) => b.popularity - a.popularity).slice(0, 5), []);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = draft.trim();
    setSearchParams(q ? { q } : {}, { replace: false });
  };

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950 px-4 md:px-10 pt-6 pb-8 md:pt-8 md:pb-10">
        <div className="[&_a]:text-background-400 [&_a:hover]:text-background-50 [&_span]:text-background-200 [&_i]:text-background-600">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
        </div>
        <div className="mt-5 md:mt-6 max-w-3xl">
          <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-accent-400 mb-2">
            <span className="w-4 h-px bg-accent-500"></span>
            Search
          </span>
          <h1 className="font-heading text-2xl md:text-[34px] leading-tight font-semibold text-background-50 mb-4">
            站内剧集搜索
          </h1>
          <form onSubmit={submit}>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex items-center flex-1 h-12 rounded-md bg-background-50 overflow-hidden">
                <span className="w-12 h-12 flex items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-lg"></i>
                </span>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  name="q"
                  aria-label="搜索关键词"
                  placeholder="输入剧名、演员、导演或类型标签"
                  className="flex-1 h-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 pr-2"
                />
                {draft && (
                  <button
                    type="button"
                    onClick={() => setDraft('')}
                    aria-label="清空关键词"
                    className="w-9 h-9 mr-1 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="h-12 px-7 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
          <p className="text-[11px] text-background-400 mt-3">
            搜索结果基于站内演示数据匹配剧名、主创与标签，并非真实网络检索。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-10 py-8 md:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-background-200">
          <div className="min-w-0">
            {keyword ? (
              <h2 className="text-sm md:text-base text-foreground-800 flex flex-wrap items-center gap-2">
                <span className="text-foreground-500">关键词</span>
                <span className="h-7 px-3 rounded-full bg-primary-100 text-primary-800 text-xs md:text-sm font-medium inline-flex items-center max-w-full truncate">
                  {keyword}
                </span>
                <span className="text-foreground-500">
                  共 <span className="text-foreground-950 font-medium">{results.length}</span> 个结果
                </span>
              </h2>
            ) : (
              <h2 className="text-sm md:text-base text-foreground-800">请输入关键词开始搜索</h2>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-foreground-400">热门标签</span>
            <div className="flex flex-wrap gap-1.5">
              {tags.slice(0, 4).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setSearchParams({ q: tag })}
                  className="h-7 px-2.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        {results.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {results.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        ) : (
          <div>
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center mb-10">
              <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                {keyword ? '未找到相关伦理剧，可尝试修改关键词' : '还没有输入搜索关键词'}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-6">
                {keyword
                  ? '换一个更短的词，或者尝试搜索演员姓名、导演姓名与类型标签，例如“家庭”“都市”“年代”。'
                  : '可以搜索剧名、导演、编剧、演员，也可以直接点击下方推荐标签。'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2.5">
                <Link
                  to="/"
                  className="h-10 px-5 rounded-md bg-primary-700 text-background-50 text-xs md:text-sm whitespace-nowrap flex items-center hover:bg-primary-600 transition-colors"
                >
                  返回首页
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setDraft('');
                    setSearchParams({}, { replace: false });
                  }}
                  className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-xs md:text-sm whitespace-nowrap flex items-center hover:border-primary-300 transition-colors cursor-pointer"
                >
                  重新输入关键词
                </button>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                不妨看看这些热门剧集
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {suggested.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}